import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ImageList,
  ImageListItem,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Palette as PaletteIcon,
  Science as ScienceIcon,
  Directions as DirectionsIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { variantService } from "../services";
import attributeService from "../services/attributeService";
import { Formik, Form, FieldArray } from "formik";
import { productVariantValidationSchema } from "../utils/validationSchemas";

const VariantManagement = ({
  product,
  open,
  onClose,
  onVariantUpdate,
}) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [variantDialog, setVariantDialog] = useState({
    open: false,
    variant: null,
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoPreview, setVideoPreview] = useState(null);
  // Removed product-level productCode handling from variants
  const [availableAttributes, setAvailableAttributes] = useState([]);

  // Generate product code on frontend (numbers only for dynamic routes)
  // No product code generation here; it belongs to Product form now

  // Custom validation function for required attributes
  const validateRequiredAttributes = (values) => {
    const errors = {};
    const requiredAttributes = availableAttributes.filter(attr => attr.isRequired);
    
    if (requiredAttributes.length > 0) {
      const missingRequired = [];
      
      requiredAttributes.forEach(requiredAttr => {
        const hasAttribute = values.attributes.some(attr => 
          attr.attributeName === requiredAttr.name
        );
        
        if (!hasAttribute) {
          missingRequired.push(requiredAttr.name);
        }
      });
      
      if (missingRequired.length > 0) {
        errors.attributes = `Missing required attributes: ${missingRequired.join(', ')}`;
      }
    }
    
    return errors;
  };

  const validationSchema = productVariantValidationSchema;

  const fetchVariants = useCallback(async () => {
    if (!product?._id) return;
    try {
      setLoading(true);
      console.log("product?._id", product?._id);
      const response = await variantService.getVariantsByProduct(product?._id);
      setVariants(response);
    } catch (error) {
      toast.error("Failed to fetch variants");
    } finally {
      setLoading(false);
    }
  }, [product?._id]);

  const fetchAvailableAttributes = useCallback(async () => {
    if (!product?._id) return;
    try {
      console.log(
        "=== FRONTEND: Fetching attributes for product:",
        product._id
      );
      const response = await attributeService.getAttributesForProduct(
        product._id
      );
      console.log("=== FRONTEND: Attributes response:", response);
      setAvailableAttributes(response);
    } catch (error) {
      console.error("Failed to fetch attributes:", error);
    }
  }, [product?._id]);

  useEffect(() => {
    if (open && product) {
      fetchVariants();
      fetchAvailableAttributes();
    }
  }, [open, product, fetchVariants, fetchAvailableAttributes]);


  const handleCreateVariant = async (values, { setSubmitting, resetForm }) => {
    if (!product?._id) {
      toast.error("Product information is missing");
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("productId", product._id);
      formData.append("attributes", JSON.stringify(values.attributes));
      formData.append("price", parseFloat(values.price).toString());
      formData.append("stock", parseInt(values.stock).toString());

      // Always send keyIngredients (even if empty array)
      formData.append(
        "keyIngredients",
        JSON.stringify(values.keyIngredients || [])
      );

      // Always send howToUse (even if empty array)
      formData.append("howToUse", JSON.stringify(values.howToUse || []));

      formData.append("sku", values.sku || "");
      formData.append("isDefault", values.isDefault || false);

      if (values.images && values.images.length > 0) {
        values.images.forEach((image) => {
          formData.append("images", image);
        });
      }

      // Handle existing images (keep track of which ones to keep)
      if (values.existingImages && values.existingImages.length > 0) {
        formData.append("existingImages", JSON.stringify(values.existingImages));
      }

      if (values.video) {
        formData.append("video", values.video);
      }

      await variantService.createVariant(formData);

      toast.success("Variant created successfully");
      setVariantDialog({ open: false, variant: null });
      resetForm();
      setImagePreviews([]);
      setVideoPreview(null);
      fetchVariants();
      onVariantUpdate && onVariantUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create variant");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateVariant = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      formData.append("attributes", JSON.stringify(values.attributes));
      formData.append("price", parseFloat(values.price).toString());
      formData.append("stock", parseInt(values.stock).toString());

      // Always send keyIngredients (even if empty array)
      formData.append(
        "keyIngredients",
        JSON.stringify(values.keyIngredients || [])
      );

      // Always send howToUse (even if empty array)
      formData.append("howToUse", JSON.stringify(values.howToUse || []));

      formData.append("sku", values.sku || "");
      formData.append("isActive", values.isActive);
      formData.append("isDefault", values.isDefault || false);

      if (values.existingImages) {
        formData.append(
          "existingImages",
          JSON.stringify(values.existingImages)
        );
      }

      // Only append images if there are new images to upload
      if (values.images && values.images.length > 0) {
        values.images.forEach((image) => {
          formData.append("images", image);
        });
      }

      if (values.existingVideo) {
        formData.append("existingVideo", values.existingVideo);
      }

      if (values.video) {
        formData.append("video", values.video);
      }

      await variantService.updateVariant(variantDialog.variant._id, formData);

      toast.success("Variant updated successfully");
      setVariantDialog({ open: false, variant: null });
      setImagePreviews([]);
      setVideoPreview(null);
      fetchVariants();
      onVariantUpdate && onVariantUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update variant");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (window.confirm("Are you sure you want to delete this variant?")) {
      try {
        await variantService.deleteVariant(variantId);
        toast.success("Variant deleted successfully");
        fetchVariants();
        onVariantUpdate && onVariantUpdate();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to delete variant"
        );
      }
    }
  };

  const handleSetAsDefault = async (variantId) => {
    try {
      await variantService.setAsDefault(variantId);
      toast.success("Variant set as default successfully");
      fetchVariants();
      onVariantUpdate && onVariantUpdate();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to set variant as default"
      );
    }
  };

  const handleStockUpdate = async (variantId, operation, amount) => {
    try {
      await variantService.updateVariantStock(variantId, {
        operation,
        stock: amount,
      });
      toast.success("Stock updated successfully");
      fetchVariants();
      onVariantUpdate && onVariantUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update stock");
    }
  };

  const openCreateDialog = () => {
    setVariantDialog({ open: true, variant: null });
    setImagePreviews([]);
  };

  const openEditDialog = (variant) => {
    setVariantDialog({ open: true, variant });
    // Don't set imagePreviews for existing images - they will be handled by existingImages
    setImagePreviews([]);
    setVideoPreview(
      variant.video ? `${process.env.REACT_APP_UPLOAD_URL}/${variant.video}` : null
    );
  };

  const getTotalImageCount = (values) => {
    const existingCount = values.existingImages?.length || 0;
    const newCount = values.images?.length || 0;
    return existingCount + newCount;
  };

  const getAllImages = (values) => {
    const allImages = [];
    
    // Add existing images
    if (values.existingImages && values.existingImages.length > 0) {
      values.existingImages.forEach((image, index) => {
        allImages.push({
          key: `existing-${index}`,
          src: `${process.env.REACT_APP_UPLOAD_URL}/${image}`,
          type: 'existing',
          index: index
        });
      });
    }
    
    // Add new images
    if (imagePreviews && imagePreviews.length > 0) {
      imagePreviews.forEach((preview, index) => {
        allImages.push({
          key: `new-${index}`,
          src: preview,
          type: 'new',
          index: index
        });
      });
    }
    
    return allImages;
  };

  const extractFirstErrorMessage = (error) => {
    if (!error) return undefined;
    if (typeof error === "string") return error;
    if (Array.isArray(error)) {
      for (const item of error) {
        const message = extractFirstErrorMessage(item);
        if (message) return message;
      }
      return undefined;
    }
    if (typeof error === "object") {
      for (const value of Object.values(error)) {
        const message = extractFirstErrorMessage(value);
        if (message) return message;
      }
    }
    return undefined;
  };

  const handleImageChange = (event, setFieldValue, setFieldTouched, values) => {
    const files = Array.from(event.currentTarget.files);
    setFieldTouched("images", true, true);
    if (files.length > 0) {
      const currentTotal = getTotalImageCount(values);
      const availableSlots = 5 - currentTotal;
      const filesToAdd = files.slice(0, availableSlots);
      
      if (filesToAdd.length > 0) {
        const newImages = [...(values.images || []), ...filesToAdd];
        const newPreviews = [...imagePreviews, ...filesToAdd.map((file) => URL.createObjectURL(file))];
        setFieldValue("images", newImages);
        setImagePreviews(newPreviews);
      } else if (files.length > availableSlots && availableSlots <= 0) {
        // Ensure validation triggers when user tries to add more than allowed
        setFieldValue("images", values.images || []);
      }
    }
  };

  const handleRemoveAnyImage = (displayIndex, setFieldValue, setFieldTouched, values) => {
    const existingCount = values.existingImages?.length || 0;
    
    if (displayIndex < existingCount) {
      // Remove existing image
      const newExistingImages = values.existingImages.filter((_, i) => i !== displayIndex);
      setFieldValue("existingImages", newExistingImages);
    } else {
      // Remove new image
      const newImageIndex = displayIndex - existingCount;
      const newImages = values.images.filter((_, i) => i !== newImageIndex);
      const newPreviews = imagePreviews.filter((_, i) => i !== newImageIndex);
      setFieldValue("images", newImages);
      setImagePreviews(newPreviews);
    }
    setFieldTouched("images", true, true);
  };

  const handleVideoChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setFieldValue("video", file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const getInitialValues = () => {
    const normalizeAttributes = (attrs = []) =>
      (attrs || []).map((attr) => ({
        ...attr,
        colorHex:
          attr.attributeType === "color-picker"
            ? attr.colorHex || "#000000"
            : undefined,
      }));

    if (variantDialog.variant) {
      const variant = variantDialog.variant;
      return {
        attributes: normalizeAttributes(variant.attributes || []),
        price: variant.priceOverride || 0,
        stock: variant.stock || 0,
        keyIngredients: variant.keyIngredients || [],
        howToUse: variant.howToUse || [],
        sku: variant.sku || "",
        images: [],
        existingImages: variant.images || [],
        video: null,
        existingVideo: variant.video || null,
        isActive: variant.isActive ?? true,
        isDefault: variant.isDefault || false,
      };
    }
    
    // For new variants, automatically include required attributes
    const requiredAttributes = availableAttributes
      .filter(attr => attr.isRequired)
      .map(attr => ({
        attributeName: attr.name,
        attributeType: attr.inputType,
        value: attr.inputType === 'multi-select' ? [] : '',
        values: attr.inputType === 'multi-select' ? [] : undefined,
        colorHex: attr.inputType === 'color-picker' ? '#000000' : undefined,
      }));
    
    return {
      attributes: normalizeAttributes(requiredAttributes),
      price: 0,
      stock: 0,
      keyIngredients: [],
      howToUse: [],
      sku: "",
      images: [],
      existingImages: [],
      video: null,
      existingVideo: null,
      isActive: true,
      isDefault: false,
    };
  };

  // Early return if product is null
  if (!product) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Manage Variants for "{product?.title}"</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Variant Cards Grid */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "right" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                size="large"
              >
                Add New Variant
              </Button>
            </Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {variants.map((variant) => (
                <Grid item xs={12} md={6} lg={4} key={variant._id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <PaletteIcon color="primary" />
                        <Typography variant="h6" component="div">
                          {variant.attributes && variant.attributes.length > 0
                            ? variant.attributes
                                .map((attr) => {
                                  if (attr.values && attr.values.length > 0) {
                                    return `${
                                      attr.attributeName
                                    }: ${attr.values.join(", ")}`;
                                  } else {
                                    return `${attr.attributeName}: ${attr.value}`;
                                  }
                                })
                                .join(", ")
                            : "No attributes"}
                        </Typography>
                        <Chip
                          label={variant.isActive ? "Active" : "Inactive"}
                          color={variant.isActive ? "success" : "error"}
                          size="small"
                        />
                        {variant.isDefault && (
                          <Chip
                            label="Default"
                            color="primary"
                            size="small"
                            icon={<StarIcon />}
                          />
                        )}
                      </Box>

                      <Typography color="text.secondary" gutterBottom>
                        Price: ₹{variant.finalPrice}
                      </Typography>
                      <Typography color="text.secondary" gutterBottom>
                        Stock: {variant.stock}
                      </Typography>
                      {variant.sku && (
                        <Typography color="text.secondary" gutterBottom>
                          SKU: {variant.sku}
                        </Typography>
                      )}

                      {variant.images && variant.images.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <ImageList
                            sx={{ width: "100%", height: 100 }}
                            cols={3}
                            rowHeight={100}
                          >
                            {variant.images.slice(0, 3).map((image, index) => (
                              <ImageListItem key={index}>
                                <img
                                  src={`${process.env.REACT_APP_UPLOAD_URL}/${image}`}
                                  alt={`${variant.color} variant`}
                                  loading="lazy"
                                />
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </Box>
                      )}

                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2">
                            Key Ingredients
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {variant.keyIngredients &&
                          variant.keyIngredients.length > 0 ? (
                            <List dense>
                              {variant.keyIngredients.map(
                                (ingredient, index) => (
                                  <ListItem key={index}>
                                    <ListItemText primary={ingredient} />
                                  </ListItem>
                                )
                              )}
                            </List>
                          ) : (
                            <Typography color="text.secondary">
                              No ingredients listed
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>

                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2">
                            How to Use
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {variant.howToUse && variant.howToUse.length > 0 ? (
                            <List dense>
                              {variant.howToUse.map((step, index) => (
                                <ListItem key={index}>
                                  <ListItemText
                                    primary={`Step ${index + 1}: ${step.step}`}
                                    secondary={step.description}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography color="text.secondary">
                              No usage instructions
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>

                      {/* Product code UI removed from variant card */}
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => openEditDialog(variant)}
                      >
                        Edit
                      </Button>
                      {!variant.isDefault && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<StarBorderIcon />}
                          onClick={() => handleSetAsDefault(variant._id)}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteVariant(variant._id)}
                        disabled={variant.isDefault && product?.isActive}
                      >
                        Delete
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          const amount = prompt("Enter amount to add:");
                          if (amount && !isNaN(amount)) {
                            handleStockUpdate(
                              variant._id,
                              "add",
                              parseInt(amount)
                            );
                          }
                        }}
                      >
                        Add Stock
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Add New Variant Button - Now positioned below the variant cards */}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Variant Form Dialog */}
      <Dialog
        open={variantDialog.open}
        onClose={() => setVariantDialog({ open: false, variant: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {variantDialog.variant ? "Edit Variant" : "Create New Variant"}
        </DialogTitle>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          validate={validateRequiredAttributes}
          onSubmit={
            variantDialog.variant ? handleUpdateVariant : handleCreateVariant
          }
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => {
            const imageError = touched.images
              ? extractFirstErrorMessage(errors.images)
              : undefined;

            return (
              <Form>
              {console.log(values, errors, "this is the values")}
              <DialogContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      <PaletteIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      Attributes
                      {availableAttributes.filter(attr => attr.isRequired).length > 0 && (
                        <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                          * Required attributes are automatically added
                        </Typography>
                      )}
                    </Typography>
                    <FieldArray name="attributes">
                      {({ push, remove, form }) => (
                        <Box>
                          {form.values.attributes.map((attribute, index) => {
                            const attributeType = availableAttributes.find(
                              (attr) => attr.name === attribute.attributeName
                            );
                            const isRequired = attributeType?.isRequired || false;
                            return (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  gap: 2,
                                  mb: 2,
                                  alignItems: "center",
                                  border: isRequired ? '1px solid #ff9800' : '1px solid transparent',
                                  borderRadius: 1,
                                  p: 1,
                                  backgroundColor: isRequired ? '#fff3e0' : 'transparent'
                                }}
                              >
                                <FormControl
                                  sx={{ minWidth: 120 }}
                                  error={
                                    !!(
                                      errors.attributes?.[index]
                                        ?.attributeName &&
                                      touched.attributes?.[index]?.attributeName
                                    )
                                  }
                                >
                                  <InputLabel>
                                    Attribute Name {isRequired && <span style={{color: 'red'}}>*</span>}
                                  </InputLabel>
                                  <Select
                                    name={`attributes.${index}.attributeName`}
                                    value={attribute.attributeName}
                                    onChange={(e) => {
                                      const selectedAttr =
                                        availableAttributes.find(
                                          (attr) => attr.name === e.target.value
                                        );
                                      handleChange(e);
                                      if (selectedAttr) {
                                        setFieldValue(
                                          `attributes.${index}.attributeType`,
                                          selectedAttr.inputType
                                        );
                                      if (selectedAttr.inputType === "color-picker") {
                                        setFieldValue(
                                          `attributes.${index}.colorHex`,
                                          attribute.colorHex || "#000000"
                                        );
                                      } else {
                                        setFieldValue(
                                          `attributes.${index}.colorHex`,
                                          undefined
                                        );
                                      }
                                      }
                                    }}
                                    onBlur={handleBlur}
                                  >
                                    {availableAttributes.map((attr) => (
                                      <MenuItem
                                        key={attr._id}
                                        value={attr.name}
                                      >
                                        {attr.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {errors.attributes?.[index]?.attributeName &&
                                    touched.attributes?.[index]
                                      ?.attributeName && (
                                      <FormHelperText>
                                        {errors.attributes[index].attributeName}
                                      </FormHelperText>
                                    )}
                                </FormControl>

                                {attributeType && (
                                  <>
                                    {attributeType.inputType ===
                                    "multi-select" ? (
                                      <FormControl
                                        sx={{ flexGrow: 1 }}
                                        error={
                                          !!(
                                            errors.attributes?.[index]
                                              ?.values &&
                                            touched.attributes?.[index]?.values
                                          )
                                        }
                                      >
                                        <InputLabel>Values</InputLabel>
                                        <Select
                                          multiple
                                          name={`attributes.${index}.values`}
                                          value={attribute.values || []}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                        >
                                          {attributeType.options?.map(
                                            (option) => (
                                              <MenuItem
                                                key={option._id}
                                                value={option.value}
                                              >
                                                {option.displayValue ||
                                                  option.value}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                        {errors.attributes?.[index]?.values &&
                                          touched.attributes?.[index]
                                            ?.values && (
                                            <FormHelperText>
                                              {errors.attributes[index].values}
                                            </FormHelperText>
                                          )}
                                      </FormControl>
                                    ) : attributeType.inputType === "select" ? (
                                      <FormControl
                                        sx={{ flexGrow: 1 }}
                                        error={
                                          !!(
                                            errors.attributes?.[index]?.value &&
                                            touched.attributes?.[index]?.value
                                          )
                                        }
                                      >
                                        <InputLabel>Value</InputLabel>
                                        <Select
                                          name={`attributes.${index}.value`}
                                          value={attribute.value}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                        >
                                          {attributeType.options?.map(
                                            (option) => (
                                              <MenuItem
                                                key={option._id}
                                                value={option.value}
                                              >
                                                {option.displayValue ||
                                                  option.value}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                        {errors.attributes?.[index]?.value &&
                                          touched.attributes?.[index]
                                            ?.value && (
                                            <FormHelperText>
                                              {errors.attributes[index].value}
                                            </FormHelperText>
                                          )}
                                      </FormControl>
                                    ) : attributeType.inputType === "color-picker" ? (
                                      <Box sx={{ display: "flex", gap: 2, flexGrow: 1 }}>
                                        <TextField
                                          name={`attributes.${index}.value`}
                                          label="Color Name"
                                          value={attribute.value || ""}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          sx={{ flexGrow: 1 }}
                                          error={
                                            !!(
                                              errors.attributes?.[index]?.value &&
                                              touched.attributes?.[index]?.value
                                            )
                                          }
                                          helperText={
                                            errors.attributes?.[index]?.value &&
                                            touched.attributes?.[index]?.value
                                              ? errors.attributes[index].value
                                              : ""
                                          }
                                        />
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            minWidth: 100,
                                          }}
                                        >
                                          <TextField
                                            name={`attributes.${index}.colorHex`}
                                            label="Color"
                                            type="color"
                                            value={attribute.colorHex || "#000000"}
                                            onChange={(event) =>
                                              setFieldValue(
                                                `attributes.${index}.colorHex`,
                                                event.target.value
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `attributes.${index}.colorHex`,
                                                true,
                                                true
                                              )
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ width: 80 }}
                                          />
                                          {errors.attributes?.[index]?.colorHex &&
                                            touched.attributes?.[index]?.colorHex && (
                                              <FormHelperText error>
                                                {errors.attributes[index].colorHex}
                                              </FormHelperText>
                                            )}
                                        </Box>
                                      </Box>
                                    ) : (
                                      <TextField
                                        name={`attributes.${index}.value`}
                                        label="Value"
                                        value={attribute.value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        sx={{ flexGrow: 1 }}
                                        type={
                                          attributeType.inputType === "number"
                                            ? "number"
                                            : "text"
                                        }
                                        error={
                                          !!(
                                            errors.attributes?.[index]?.value &&
                                            touched.attributes?.[index]?.value
                                          )
                                        }
                                        helperText={
                                          errors.attributes?.[index]?.value &&
                                          touched.attributes?.[index]?.value
                                            ? errors.attributes[index].value
                                            : ""
                                        }
                                      />
                                    )}
                                  </>
                                )}

                                <IconButton
                                  onClick={() => {
                                    if (!isRequired) {
                                      remove(index);
                                    } else {
                                      toast.warning('Cannot remove required attribute');
                                    }
                                  }}
                                  color="error"
                                  size="small"
                                  disabled={isRequired}
                                  title={isRequired ? 'Required attribute cannot be removed' : 'Remove attribute'}
                                >
                                  <RemoveCircleIcon />
                                </IconButton>
                              </Box>
                            );
                          })}
                          <Button
                            type="button"
                            onClick={() =>
                              push({
                                attributeName: "",
                                attributeType: "",
                                value: "",
                                values: [],
                            colorHex: "#000000",
                              })
                            }
                            startIcon={<AddCircleIcon />}
                            variant="outlined"
                            size="small"
                          >
                            Add Attribute
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                    
                    {/* Required Attributes Validation Error */}
                    {errors.attributes && typeof errors.attributes === 'string' && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="error">
                          {errors.attributes}
                        </Typography>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Price"
                          name="price"
                          type="number"
                          value={values.price}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setFieldValue("price", value);
                          }}
                          onBlur={handleBlur}
                          error={touched.price && Boolean(errors.price)}
                          helperText={touched.price && errors.price}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Stock"
                          name="stock"
                          type="number"
                          value={values.stock}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            setFieldValue("stock", value);
                          }}
                          onBlur={handleBlur}
                          error={touched.stock && Boolean(errors.stock)}
                          helperText={touched.stock && errors.stock}
                          margin="normal"
                        />
                      </Grid>
                    </Grid>
                    {/* <TextField
                      fullWidth
                      label="SKU"
                      name="sku"
                      value={values.sku}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      margin="normal"
                    /> */}
                    {/* <FormControlLabel
                      control={
                        <Switch
                          checked={values.isActive}
                          onChange={(e) =>
                            setFieldValue("isActive", e.target.checked)
                          }
                        />
                      }
                      label="Active"
                    /> */}
                    {/* <FormControlLabel
                      control={
                        <Switch
                          checked={values.isDefault || false}
                          onChange={(e) =>
                            setFieldValue("isDefault", e.target.checked)
                          }
                          disabled={!values.isActive}
                        />
                      }
                      label="Set as Default Variant"
                    /> */}

                    {/* Key Ingredients */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                      <ScienceIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      Key Ingredients
                    </Typography>
                    <FieldArray name="keyIngredients">
                      {({ push, remove }) => (
                        <Box>
                          {values.keyIngredients.length === 0 ? (
                            <Box
                              sx={{
                                mb: 2,
                                p: 2,
                                bgcolor: "grey.50",
                                borderRadius: 1,
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                No ingredients added yet
                              </Typography>
                              <Button
                                startIcon={<AddCircleIcon />}
                                onClick={() => push("")}
                                variant="outlined"
                                size="small"
                              >
                                Add First Ingredient
                              </Button>
                            </Box>
                          ) : (
                            <>
                              {values.keyIngredients.map(
                                (ingredient, index) => (
                                  <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                  >
                                    <TextField
                                      fullWidth
                                      value={ingredient}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `keyIngredients.${index}`,
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter ingredient"
                                    />
                                    <IconButton
                                      onClick={() => remove(index)}
                                      color="error"
                                    >
                                      <RemoveCircleIcon />
                                    </IconButton>
                                  </Box>
                                )
                              )}
                              <Button
                                startIcon={<AddCircleIcon />}
                                onClick={() => push("")}
                                variant="outlined"
                                size="small"
                              >
                                Add Ingredient
                              </Button>
                            </>
                          )}
                        </Box>
                      )}
                    </FieldArray>

                    {/* How to Use */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                      <DirectionsIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      How to Use
                    </Typography>
                    <FieldArray name="howToUse">
                      {({ push, remove }) => (
                        <Box>
                          {values.howToUse.length === 0 ? (
                            <Box
                              sx={{
                                mb: 2,
                                p: 2,
                                bgcolor: "grey.50",
                                borderRadius: 1,
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                No steps added yet
                              </Typography>
                              <Button
                                startIcon={<AddCircleIcon />}
                                onClick={() =>
                                  push({ step: "", description: "" })
                                }
                                variant="outlined"
                                size="small"
                              >
                                Add First Step
                              </Button>
                            </Box>
                          ) : (
                            <>
                              {values.howToUse.map((step, index) => (
                                <Card key={index} sx={{ mb: 2 }}>
                                  <CardContent>
                                    <TextField
                                      fullWidth
                                      label="Step"
                                      value={step.step}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `howToUse.${index}.step`,
                                          e.target.value
                                        )
                                      }
                                      margin="normal"
                                    />
                                    <TextField
                                      fullWidth
                                      label="Description"
                                      multiline
                                      rows={2}
                                      value={step.description}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `howToUse.${index}.description`,
                                          e.target.value
                                        )
                                      }
                                      margin="normal"
                                    />
                                    <Button
                                      onClick={() => remove(index)}
                                      color="error"
                                      size="small"
                                      startIcon={<RemoveCircleIcon />}
                                    >
                                      Remove Step
                                    </Button>
                                  </CardContent>
                                </Card>
                              ))}
                              <Button
                                startIcon={<AddCircleIcon />}
                                onClick={() =>
                                  push({ step: "", description: "" })
                                }
                                variant="outlined"
                                size="small"
                              >
                                Add Step
                              </Button>
                            </>
                          )}
                        </Box>
                      )}
                    </FieldArray>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {/* Product Code field removed from variant form */}

                    {/* Images */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                      <ImageIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      Variant Images (Max 5)
                    </Typography>
                    
                    {/* Upload Images Button */}
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="variant-image-upload"
                      type="file"
                      multiple
                      onChange={(e) =>
                        handleImageChange(e, setFieldValue, setFieldTouched, values)
                      }
                    />
                    <label htmlFor="variant-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<ImageIcon />}
                        disabled={getTotalImageCount(values) >= 5}
                      >
                        Upload Images
                      </Button>
                    </label>

                    {/* Image List Display */}
                    {getAllImages(values).length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {getAllImages(values).map((imageData, index) => (
                            <Box
                              key={imageData.key}
                              sx={{
                                position: "relative",
                                display: "inline-block",
                              }}
                            >
                              <img
                                src={imageData.src}
                                alt={`Variant ${index + 1}`}
                                style={{
                                  width: 100,
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                  border: index === 0 ? "2px solid #1976d2" : "1px solid #e0e0e0",
                                }}
                              />
                              {/* Main Image Chip */}
                              {index === 0 && (
                                <Chip
                                  label="Main"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    left: 4,
                                    fontSize: "0.7rem",
                                    height: 20,
                                  }}
                                />
                              )}
                              {/* Delete Button */}
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                  },
                                }}
                                onClick={() =>
                                  handleRemoveAnyImage(
                                    index,
                                    setFieldValue,
                                    setFieldTouched,
                                    values
                                  )
                                }
                              >
                                <DeleteIcon fontSize="small" color="error" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                        
                        {/* Info Text */}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          The first image will be treated as the main image. Maximum 5 images allowed.
                        </Typography>
                      </Box>
                    )}
                    {imageError && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {imageError}
                      </FormHelperText>
                    )}

                    {/* Video Upload Section */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Product Video (Optional)
                      </Typography>
                      <input
                        accept="video/*"
                        style={{ display: "none" }}
                        id="variant-video-upload"
                        type="file"
                        onChange={(e) => handleVideoChange(e, setFieldValue)}
                      />
                      <label htmlFor="variant-video-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<VideoIcon />}
                        >
                          Upload Video
                        </Button>
                      </label>
                      {videoPreview && (
                        <Box sx={{ mt: 2 }}>
                          <video
                            src={videoPreview}
                            controls
                            style={{
                              width: "100%",
                              maxWidth: 300,
                              height: "auto",
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() =>
                    setVariantDialog({ open: false, variant: null })
                  }
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : variantDialog.variant
                    ? "Update"
                    : "Create"}
                </Button>
              </DialogActions>
            </Form>
            );
          }}
        </Formik>
      </Dialog>
    </Dialog>
  );
};

export default VariantManagement;
